import {
	convertSortDirectionToDto,
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { createRemoteDataSource, LazyLoadCursorKind } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import {
	ActiveFilter,
	HubItemIsNewFilter,
	hubItemIsNewToFlag
} from "../../common";
import {
	KnowledgeItemOrderModel,
	KnowledgeItemPagingInformationModel,
	KnowledgeItemPreviewItemModel,
	KnowledgeItemSortProperty,
	list
} from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";

export type KnowledgeItemsFilter = {
	categoryId: number | null;
	activeFilter: ActiveFilter | null;
	isPinned: boolean | null;
	isNewFilter: HubItemIsNewFilter;
};

export const useDataSource = createRemoteDataSource<
	KnowledgeItemPreviewItemModel,
	KnowledgeItemsFilter
>()
	.setConfiguration({
		hasSearch: true,
		hasFiltering: true,
		lazyLoadCursor: LazyLoadCursorKind.AfterLastItem,
	})
	.getHook(
		{
			performRequest: async (options): Promise<LazyLoadPageResult<KnowledgeItemPreviewItemModel>> => {
				const {
					after,
					amount,
					filterState,
					searchTerm,
					sorting,
					signal
				} = options;

				const {
					categoryId,
					activeFilter,
					isPinned,
					isNewFilter
				} = filterState;

				const [authorId, tagId] = createFilters(activeFilter);

				const order: KnowledgeItemOrderModel | null = createOrder(sorting);

				const pagingInformation: KnowledgeItemPagingInformationModel | null = createPagingInformation(after);

				const response: LazyLoadPageResult<KnowledgeItemPreviewItemModel> = await list(
					{
						categoryId,
						authorId,
						tagId,
						searchTerm,
						isPinned,
						order,
						pagingInformation,
						amount,
						isNew: hubItemIsNewToFlag[isNewFilter]
					},
					signal
				);

				return response;
			}
		}
	);

export function createFilters(
	activeFilter: ActiveFilter | null
): [authorId: number | null, tagId: number | null] {
	let authorId: number | null = null;
	let tagId: number | null = null;

	if (activeFilter) {
		switch (activeFilter.kind) {
			case "author":
				authorId = activeFilter.value
					.id;
				break;
			case "tag":
				tagId = activeFilter.value
					.id;
				break;
			default:
				never(activeFilter);
		}
	}

	return [authorId, tagId];
}

const propertyFilterMap: Partial<Record<PropertyPath<KnowledgeItemPreviewItemModel>, KnowledgeItemSortProperty>> = {
	["publicationDate"]: KnowledgeItemSortProperty.PublicationDate,
	["viewsCount"]: KnowledgeItemSortProperty.Views,
	["title"]: KnowledgeItemSortProperty.Title,
	["pinDate"]: KnowledgeItemSortProperty.PinDate
};

export function createOrder(
	sorting: DataSourceSortableState<KnowledgeItemPreviewItemModel> | undefined
): KnowledgeItemOrderModel | null {
	let order: KnowledgeItemOrderModel | null = null;

	if (sorting && sorting.direction !== DataSourceSortDirection.None) {
		const property: KnowledgeItemSortProperty | undefined = propertyFilterMap[sorting.propertyName];
		if (isNil(property)) {
			throw new Error(`Sorting on '${sorting.propertyName}' is not supported`);
		}

		order = {
			property,
			direction: convertSortDirectionToDto(sorting.direction)
		};
	}

	return order;
}

export function createPagingInformation(
	after: KnowledgeItemPreviewItemModel | undefined
): KnowledgeItemPagingInformationModel | null {
	let pagingInformation: KnowledgeItemPagingInformationModel | null = null;

	if (after) {
		pagingInformation = {
			id: after.id,
			publicationDate: after.publicationDate,
			title: after.title,
			viewsCount: after.viewsCount,
			pinDate: after.pinDate,
			relevanceScore: after.relevanceScore
		};
	}

	return pagingInformation;
}
